// src/pages/admin/AdminUsers.jsx
import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, Loader2, ChevronDown, ChevronUp, Mail, Phone, MapPin, Calendar, ShoppingBag, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../../services/supabase';

const UsersStyles = () => (
  <style>{`
    .page-header-flex {
      display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;
    }
    .page-title { font-size: 1.5rem; font-weight: 700; color: var(--saas-text-primary); margin: 0; }
    
    .btn-primary {
      background: var(--saas-primary); color: white; border: none; padding: 10px 20px;
      border-radius: 6px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
      transition: background 0.2s;
    }
    .btn-primary:hover { background: #245F48; }
    .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
    
    .btn-outline {
      background: white; color: var(--saas-text-primary); border: 1px solid var(--saas-border-color);
      padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
      transition: background 0.2s;
    }
    .btn-outline:hover { background: var(--saas-primary-light); }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 16px;
      margin-bottom: 24px;
    }
    .stat-card {
      background: white;
      padding: 20px;
      border-radius: var(--saas-radius);
      border: 1px solid var(--saas-border-color);
    }
    .stat-label {
      font-size: 0.85rem;
      color: var(--saas-text-secondary);
      margin-bottom: 8px;
    }
    .stat-value {
      font-size: 1.75rem;
      font-weight: 700;
      color: var(--saas-text-primary);
    }

    .toolbar-card {
      background: white; padding: 16px; border-radius: var(--saas-radius) var(--saas-radius) 0 0;
      border: 1px solid var(--saas-border-color); border-bottom: none;
      display: flex; justify-content: space-between; flex-wrap: wrap; gap: 16px;
    }
    .search-box-clean {
      position: relative; width: 320px;
    }
    .search-input-clean {
      width: 100%; padding: 10px 10px 10px 42px;
      border: 1px solid var(--saas-border-color); border-radius: 6px;
      background: white; color: var(--saas-text-primary); outline: none;
      transition: border-color 0.2s;
    }
    .search-input-clean:focus { border-color: var(--saas-primary); }
    .search-icon { position: absolute; left: 12px; top: 12px; color: var(--saas-text-secondary); }

    .table-container {
      background: white; border-radius: 0 0 var(--saas-radius) var(--saas-radius);
      border: 1px solid var(--saas-border-color); overflow-x: auto;
    }
    .saas-table { width: 100%; border-collapse: collapse; background: white; }
    
    .saas-table th {
      text-align: left; padding: 16px 24px;
      border-bottom: 1px solid var(--saas-border-color);
      color: var(--saas-text-secondary); font-size: 0.75rem; text-transform: uppercase; font-weight: 700; letter-spacing: 0.8px;
      white-space: nowrap; background: white;
    }
    .saas-table td {
      padding: 16px 24px; border-bottom: 1px solid var(--saas-border-color);
      color: var(--saas-text-primary); font-size: 0.9rem; background: white;
    }
    
    /* ✅ 修正：移除 hover 藍色邊框，改用淡淡的背景色 */
    .user-row {
      cursor: pointer;
      transition: background-color 0.2s;
    }
    .user-row:hover td {
      background: var(--saas-primary-light) !important;
    }
    .user-row.expanded td {
      background: white;
      border-bottom-color: transparent;
    }

    .user-cell {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .user-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: linear-gradient(135deg, #2F7D5E 0%, #48A67C 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: 600;
      font-size: 1rem;
      flex-shrink: 0;
    }
    .user-info {
      flex: 1;
    }
    .user-name { font-weight: 600; }
    .user-email { font-size: 0.8rem; color: var(--saas-text-secondary); }

    .badge {
      display: inline-flex; align-items: center; padding: 4px 12px;
      border-radius: 9999px; font-size: 0.75rem; font-weight: 700; line-height: 1;
      gap: 4px;
    }
    .badge-active { background: #f0fdf4; color: #15803d; }
    .badge-inactive { background: #fef2f2; color: #b91c1c; }
    .badge-verified { background: #dbeafe; color: #1e40af; }
    .badge-unverified { background: #fff7ed; color: #c2410c; }

    .expand-icon {
      color: var(--saas-primary);
      transition: transform 0.2s;
    }
    .expand-icon.expanded {
      transform: rotate(180deg);
    }

    .expanded-row {
      background: white;
    }
    .expanded-content {
      padding: 24px;
      border-top: 1px solid var(--saas-border-color);
      background: white;
    }
    .detail-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 24px;
    }
    .detail-section {
      background: white;
      padding: 16px;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
    }
    .detail-section-title {
      font-size: 0.85rem; font-weight: 700; color: var(--saas-text-secondary);
      text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 12px;
      display: flex; align-items: center; gap: 8px;
    }
    .detail-row {
      display: flex; justify-content: space-between; padding: 8px 0;
      font-size: 0.9rem;
      align-items: flex-start;
    }
    .detail-label { 
      color: var(--saas-text-secondary); 
      display: flex; 
      align-items: center; 
      gap: 6px;
      flex-shrink: 0;
    }
    .detail-value { 
      color: var(--saas-text-primary); 
      font-weight: 500;
      text-align: right;
      word-break: break-word;
    }

    .order-history {
      margin-top: 16px;
    }
    .order-item {
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 12px;
      margin-bottom: 8px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .order-item:last-child { margin-bottom: 0; }
    .order-number { font-family: monospace; font-weight: 600; color: var(--saas-primary); }
    .order-date { font-size: 0.8rem; color: var(--saas-text-secondary); }
    .order-amount { font-weight: 700; }

    .loading-state, .empty-state {
      padding: 60px 20px; text-align: center; color: var(--saas-text-secondary);
      background: white;
    }
    .loading-spinner {
      animation: spin 1s linear infinite; margin: 0 auto 16px;
    }
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    .filter-dropdown { position: relative; display: inline-block; }
    .filter-menu {
      position: absolute; top: 100%; right: 0; margin-top: 8px;
      background: white; border: 1px solid var(--saas-border-color);
      border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      padding: 8px; min-width: 180px; z-index: 10;
    }
    .filter-option {
      padding: 8px 12px; cursor: pointer; border-radius: 4px;
      font-size: 0.9rem; transition: background 0.2s;
    }
    .filter-option:hover { background: var(--saas-primary-light); }
    .filter-option.active { background: #d1fae5; color: var(--saas-primary); font-weight: 600; }

    .preferences-display {
      font-size: 0.85rem;
      color: var(--saas-text-secondary);
      font-style: italic;
      max-width: 200px;
      word-break: break-word;
    }
  `}</style>
);

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [userOrders, setUserOrders] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [expandedUsers, setExpandedUsers] = useState(new Set());

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;

      setUsers(usersData || []);

      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('user_id, total_amount, created_at, order_number, payment_status');

      if (!ordersError && ordersData) {
        const ordersByUser = {};
        ordersData.forEach(order => {
          if (!ordersByUser[order.user_id]) {
            ordersByUser[order.user_id] = [];
          }
          ordersByUser[order.user_id].push(order);
        });
        setUserOrders(ordersByUser);
      }

    } catch (error) {
      alert('無法載入用戶資料：' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpand = async (userId) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
      if (!userOrders[userId]) {
        await fetchUserOrders(userId);
      }
    }
    setExpandedUsers(newExpanded);
  };

  const fetchUserOrders = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;

      setUserOrders(prev => ({
        ...prev,
        [userId]: data || []
      }));
    } catch (error) {
      // silently ignore
    }
  };

  const filteredUsers = users.filter(user => {
    if (statusFilter === 'active' && !user.is_active) {
      return false;
    }
    if (statusFilter === 'verified' && !user.is_verified) {
      return false;
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const name = (user.name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      const phone = (user.phone_number || '').toLowerCase();
      
      return name.includes(searchLower) || 
             email.includes(searchLower) || 
             phone.includes(searchLower);
    }

    return true;
  });

  const handleExport = () => {
    if (filteredUsers.length === 0) {
      alert('沒有用戶可以匯出');
      return;
    }

    const headers = ['姓名', 'Email', '電話', '註冊日期', '訂單數量', '總消費', '已驗證', '狀態'];
    const rows = filteredUsers.map(user => [
      user.name || '-',
      user.email || '-',
      user.phone_number || '-',
      new Date(user.created_at).toLocaleDateString('zh-TW'),
      user.total_orders || 0,
      `$${(user.total_spent || 0).toFixed(2)}`,
      user.is_verified ? '是' : '否',
      user.is_active ? '活躍' : '未活躍'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.is_active).length;
  const verifiedUsers = users.filter(u => u.is_verified).length;
  const totalOrders = users.reduce((sum, u) => sum + (u.total_orders || 0), 0);
  const totalRevenue = users.reduce((sum, u) => sum + (u.total_spent || 0), 0);

  const getUserInitial = (user) => {
    if (user.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getUserOrders = (userId) => {
    return userOrders[userId] || [];
  };

  // ✅ 修正：處理 default_address 可能是物件的情況
  const formatAddress = (address) => {
    if (!address) return '未設定';
    
    // 如果是物件
    if (typeof address === 'object') {
      const parts = [];
      if (address.street) parts.push(address.street);
      if (address.city) parts.push(address.city);
      if (address.province) parts.push(address.province);
      if (address.country) parts.push(address.country);
      return parts.length > 0 ? parts.join(', ') : '未設定';
    }
    
    // 如果是字串
    return address;
  };

  // ✅ 修正：處理 preferences 可能是物件的情況
  const formatPreferences = (prefs) => {
    if (!prefs) return null;
    
    if (typeof prefs === 'object') {
      return JSON.stringify(prefs, null, 2);
    }
    
    return prefs;
  };

  return (
    <>
      <UsersStyles />
      
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn-primary" onClick={handleExport} disabled={isLoading}>
          <Download size={18} /> 匯出報表
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">總會員數</div>
          <div className="stat-value">{totalUsers}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">活躍會員</div>
          <div className="stat-value">{activeUsers}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">已驗證會員</div>
          <div className="stat-value">{verifiedUsers}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">總訂單數</div>
          <div className="stat-value">{totalOrders}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">總營收</div>
          <div className="stat-value">${totalRevenue.toFixed(2)}</div>
        </div>
      </div>

      <div className="toolbar-card">
        <div className="search-box-clean">
          <Search size={18} className="search-icon" />
          <input 
            type="text" 
            className="search-input-clean" 
            placeholder="搜尋姓名、Email 或電話..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-dropdown">
          <button 
            className="btn-outline"
            onClick={() => setShowFilterMenu(!showFilterMenu)}
          >
            <Filter size={16} /> 
            篩選 {statusFilter !== 'all' && `(${statusFilter === 'active' ? '活躍' : '已驗證'})`}
          </button>
          
          {showFilterMenu && (
            <div className="filter-menu">
              <div 
                className={`filter-option ${statusFilter === 'all' ? 'active' : ''}`}
                onClick={() => { setStatusFilter('all'); setShowFilterMenu(false); }}
              >
                全部會員
              </div>
              <div 
                className={`filter-option ${statusFilter === 'active' ? 'active' : ''}`}
                onClick={() => { setStatusFilter('active'); setShowFilterMenu(false); }}
              >
                活躍會員
              </div>
              <div 
                className={`filter-option ${statusFilter === 'verified' ? 'active' : ''}`}
                onClick={() => { setStatusFilter('verified'); setShowFilterMenu(false); }}
              >
                已驗證會員
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="table-container">
        {isLoading ? (
          <div className="loading-state">
            <Loader2 size={40} className="loading-spinner" />
            <p>正在載入會員資料...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="empty-state">
            <p>沒有找到符合條件的會員</p>
          </div>
        ) : (
          <table className="saas-table">
            <thead>
              <tr>
                <th style={{width: '50px'}}></th>
                <th>會員資訊</th>
                <th>電話</th>
                <th>註冊日期</th>
                <th>訂單數</th>
                <th>總消費</th>
                <th>驗證</th>
                <th>狀態</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => {
                const orders = getUserOrders(user.id);

                return (
                  <React.Fragment key={user.id}>
                    <tr 
                      className={`user-row ${expandedUsers.has(user.id) ? 'expanded' : ''}`}
                      onClick={() => toggleExpand(user.id)}
                    >
                      <td>
                        <div className={`expand-icon ${expandedUsers.has(user.id) ? 'expanded' : ''}`}>
                          {expandedUsers.has(user.id) ? 
                            <ChevronUp size={18} /> : 
                            <ChevronDown size={18} />
                          }
                        </div>
                      </td>
                      <td>
                        <div className="user-cell">
                          <div className="user-avatar">{getUserInitial(user)}</div>
                          <div className="user-info">
                            <div className="user-name">{user.name || '未設定姓名'}</div>
                            <div className="user-email">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>{user.phone_number || '-'}</td>
                      <td style={{color: 'var(--saas-text-secondary)'}}>
                        {new Date(user.created_at).toLocaleDateString('zh-TW')}
                      </td>
                      <td><strong>{user.total_orders || 0}</strong> 筆</td>
                      <td><strong>${(user.total_spent || 0).toFixed(2)}</strong></td>
                      <td>
                        <span className={`badge ${user.is_verified ? 'badge-verified' : 'badge-unverified'}`}>
                          {user.is_verified ? (
                            <><CheckCircle size={12} /> 已驗證</>
                          ) : (
                            <><XCircle size={12} /> 未驗證</>
                          )}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${user.is_active ? 'badge-active' : 'badge-inactive'}`}>
                          {user.is_active ? '活躍' : '未活躍'}
                        </span>
                      </td>
                    </tr>

                    {expandedUsers.has(user.id) && (
                      <tr className="expanded-row">
                        <td colSpan="8">
                          <div className="expanded-content">
                            <div className="detail-grid">
                              <div className="detail-section">
                                <h3 className="detail-section-title">
                                  <Mail size={16} /> 聯絡資訊
                                </h3>
                                <div className="detail-row">
                                  <span className="detail-label">
                                    <Mail size={14} /> Email
                                  </span>
                                  <span className="detail-value">{user.email || '未提供'}</span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">
                                    <Phone size={14} /> 電話
                                  </span>
                                  <span className="detail-value">{user.phone_number || '未提供'}</span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">
                                    <MapPin size={14} /> 預設地址
                                  </span>
                                  <span className="detail-value">
                                    {formatAddress(user.default_address)}
                                  </span>
                                </div>
                              </div>

                              <div className="detail-section">
                                <h3 className="detail-section-title">
                                  <Calendar size={16} /> 帳號資訊
                                </h3>
                                <div className="detail-row">
                                  <span className="detail-label">註冊日期</span>
                                  <span className="detail-value">
                                    {new Date(user.created_at).toLocaleDateString('zh-TW')}
                                  </span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">最後更新</span>
                                  <span className="detail-value">
                                    {user.updated_at 
                                      ? new Date(user.updated_at).toLocaleDateString('zh-TW')
                                      : '未記錄'}
                                  </span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">最後訂單</span>
                                  <span className="detail-value">
                                    {user.last_order_date 
                                      ? new Date(user.last_order_date).toLocaleDateString('zh-TW')
                                      : '無訂單'}
                                  </span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">會員角色</span>
                                  <span className="detail-value">{user.role || 'customer'}</span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">帳號狀態</span>
                                  <span className="detail-value">
                                    {user.is_verified ? '✓ 已驗證' : '✗ 未驗證'} / 
                                    {user.is_active ? ' 活躍' : ' 未活躍'}
                                  </span>
                                </div>
                              </div>

                              <div className="detail-section">
                                <h3 className="detail-section-title">
                                  <ShoppingBag size={16} /> 消費統計
                                </h3>
                                <div className="detail-row">
                                  <span className="detail-label">訂單總數</span>
                                  <span className="detail-value">{user.total_orders || 0} 筆</span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">總消費金額</span>
                                  <span className="detail-value" style={{color: 'var(--saas-primary)'}}>
                                    ${(user.total_spent || 0).toFixed(2)}
                                  </span>
                                </div>
                                <div className="detail-row">
                                  <span className="detail-label">平均訂單</span>
                                  <span className="detail-value">
                                    ${user.total_orders > 0 
                                      ? ((user.total_spent || 0) / user.total_orders).toFixed(2) 
                                      : '0.00'}
                                  </span>
                                </div>
                                {user.preferences && (
                                  <div className="detail-row">
                                    <span className="detail-label">偏好設定</span>
                                    <span className="preferences-display">
                                      {formatPreferences(user.preferences)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {orders.length > 0 && (
                              <div className="detail-section order-history">
                                <h3 className="detail-section-title">
                                  <ShoppingBag size={16} /> 最近訂單（最多顯示 5 筆）
                                </h3>
                                {orders.slice(0, 5).map(order => (
                                  <div key={order.id} className="order-item">
                                    <div>
                                      <div className="order-number">{order.order_number}</div>
                                      <div className="order-date">
                                        {new Date(order.created_at).toLocaleDateString('zh-TW')}
                                      </div>
                                    </div>
                                    <div className="order-amount">${order.total_amount?.toFixed(2)}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default AdminUsers;